import {
  Alert,
  Anchor,
  Box,
  Button,
  Center,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCircleCheck,
  IconMail,
} from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/http';
import { getErrorMessage } from '../api/client';

type Status = 'loading' | 'ok' | 'expired' | 'invalid' | 'missing';

/**
 * Pagina publica de verificacion de email (Sprint 5.1).
 *
 * - Lee `?token=xxx` de la URL.
 * - Llama `GET /auth/verify-email?token=xxx`.
 * - Muestra resultado (loading / ok / expirado / invalido / sin token).
 * - En OK, redirige a /login tras 3s.
 */
export function VerificarEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [status, setStatus] = useState<Status>(token ? 'loading' : 'missing');
  const [message, setMessage] = useState<string | null>(null);
  // Fix Sprint 5.1: useRef evita que React StrictMode (dev) ejecute el
  // useEffect dos veces. Como el token es de un solo uso, la segunda
  // llamada fallaria con "Token invalido o ya utilizado" aunque la
  // primera hubiera tenido exito.
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    let cancelled = false;
    (async () => {
      try {
        await apiClient.get('/auth/verify-email', { params: { token } });
        if (!cancelled) setStatus('ok');
      } catch (e) {
        if (cancelled) return;
        const msg = getErrorMessage(e);
        // El backend distingue "expirado" vs "invalido" en el mensaje.
        if (/expir/i.test(msg)) setStatus('expired');
        else setStatus('invalid');
        setMessage(msg);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Redirigir a /login unos segundos despues del OK.
  useEffect(() => {
    if (status !== 'ok') return;
    const t = setTimeout(() => navigate('/login', { replace: true }), 3000);
    return () => clearTimeout(t);
  }, [status, navigate]);

  return (
    <Center mih="100vh" bg="deca.0">
      <Box p="xl" bg="white" style={{ borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <Stack gap="md" maw={440}>
          <Stack align="center" gap={4}>
            <IconMail size={32} color="var(--mantine-color-deca-9)" />
            <Title order={2} c="deca.9" ta="center">
              Verificar tu email
            </Title>
          </Stack>

          {status === 'loading' && (
            <Text size="sm" c="dimmed" ta="center">
              Validando tu enlace de verificacion...
            </Text>
          )}

          {status === 'ok' && (
            <Alert color="green" variant="light" icon={<IconCircleCheck size={20} />}>
              <Stack gap={4}>
                <Text fw={600}>Email verificado</Text>
                <Text size="sm">
                  Tu cuenta esta activa. En unos segundos te llevamos a la pantalla de
                  inicio de sesion.
                </Text>
              </Stack>
            </Alert>
          )}

          {status === 'expired' && (
            <>
              <Alert color="orange" variant="light" icon={<IconAlertCircle size={20} />}>
                {message ?? 'El enlace ha expirado.'}
              </Alert>
              <Text size="sm" ta="center">
                Pidele que te enviemos uno nuevo desde la pantalla de{' '}
                <Anchor component={Link} to="/login">
                  inicio de sesion
                </Anchor>
                .
              </Text>
            </>
          )}

          {status === 'invalid' && (
            <>
              <Alert color="red" variant="light" icon={<IconAlertCircle size={20} />}>
                {message ?? 'El enlace no es valido o ya se ha utilizado.'}
              </Alert>
              <Button
                component={Link}
                to="/login"
                variant="light"
                color="deca.9"
                fullWidth
              >
                Volver al inicio de sesion
              </Button>
            </>
          )}

          {status === 'missing' && (
            <Alert color="yellow" variant="light" icon={<IconAlertCircle size={20} />}>
              Falta el token de verificacion en la URL. Abre el enlace que te hemos
              enviado por email.
            </Alert>
          )}
        </Stack>
      </Box>
    </Center>
  );
}